import type { TFunction } from 'i18next';

/**
 * Determine file type from MIME type
 */
export function getFileTypeFromMimeType(mimeType: string): string {
  if (mimeType.startsWith('text/') || mimeType === 'application/json') {
    return 'code';
  }
  if (mimeType.includes('pdf') || mimeType.includes('word') || mimeType.includes('document')) {
    return 'document';
  }
  return 'file';
}

/**
 * Check if text contains keywords from a category
 */
export function hasKeywords(text: string, keywords: string[]): boolean {
  return keywords.some(keyword => text.includes(keyword));
}

/** One insight block: bold heading plus its bullets; the copy comes from the bundles. */
const insightSection = (t: TFunction, category: string, bullets: number): string => (
  `**${t(`chat.fileInsights.${category}.heading`)}**\n`
  + Array.from({ length: bullets }, (_, index) => `- ${t(`chat.fileInsights.${category}.bullet${index + 1}`)}\n`).join('')
);

/**
 * Generate technical insights from summaries
 */
export function generateTechnicalInsights(summaries: string[], t: TFunction): string {
  // Common technical keywords and patterns to look for
  const technicalKeywords = {
    materials: ['材料', '素材', 'アルミ', '鋼', '樹脂', '金属', '合金'],
    environment: ['高温', '低温', '湿度', '腐食', '環境', '温度', '圧力'],
    failure: ['不具合', '故障', '破損', '亀裂', '劣化', '漏れ', '異常'],
    design: ['設計', '仕様', '基準', '規格', '標準', '要求'],
    testing: ['試験', 'テスト', '検証', '評価', '測定', '実験'],
    process: ['工程', 'プロセス', '製造', '品質', '管理'],
    countermeasures: ['対策', '改善', '防止', '予防', '再発防止']
  };

  let insights = '';
  const allText = summaries.join(' ').toLowerCase();

  // Analyze patterns and generate insights; the markdown shape lives here, the copy in the bundles.
  const sections: Array<[keyof typeof technicalKeywords, number]> = [
    ['failure', 2],
    ['environment', 2],
    ['design', 2],
    ['testing', 2],
    ['materials', 2],
  ];
  let found = 0;
  for (const [category, bullets] of sections) {
    if (hasKeywords(allText, technicalKeywords[category])) {
      found += 1;
      insights += `${insightSection(t, category, bullets)}\n`;
    }
  }

  // Add general recommendations if no specific patterns found
  if (found === 0) {
    insights += `${insightSection(t, 'general', 2)}\n`;
  }

  // Add common recommendations
  insights += insightSection(t, 'common', 3);

  return insights;
}

/**
 * Create a combined summary from individual summaries
 */
export function createCombinedSummary(
  summaries: string[],
  fileIds: string[],
  t: TFunction,
  isDeepAnalysis = false,
): string {
  if (summaries.length === 0) return '';

  if (summaries.length === 1) {
    return summaries[0];
  }

  // Create a structured combined summary
  let combined = `## ${t('chat.combinedSummary.heading')}\n`;
  combined += `${t('chat.combinedSummary.intro', { count: fileIds.length })}\n\n`;

  // Add individual summaries with headers
  summaries.forEach((summary, index) => {
    combined += `### ${t('chat.combinedSummary.fileHeading', { index: index + 1 })}\n`;
    combined += `${summary}\n\n`;
  });

  // Only add technical insights for basic summaries, not for deep analysis
  if (!isDeepAnalysis) {
    combined += `### ${t('chat.combinedSummary.insightsHeading')}\n`;
    combined += generateTechnicalInsights(summaries, t);
  }

  return combined;
}
